import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, ChevronLeft, ChevronRight, Phone, Mail, Building2, ArrowUpDown, ArrowUp, ArrowDown, Trash2, RotateCcw, Eye, EyeOff, MapPinOff, Sparkles } from 'lucide-react';
import { getInitials } from '@/lib/status';
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog';
import ClientFormModal from '@/components/ClientFormModal';
import CityCleanupModal from '@/components/CityCleanupModal';
import IdChip from '@/components/IdChip';
import { toast } from 'sonner';

export default function ClientsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(25);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [showForm, setShowForm] = useState(false);
  const [editForm, setEditForm] = useState({});  // initial client doc passed to ClientFormModal
  const [loading, setLoading] = useState(true);
  // Iter178 — soft-delete view modes.
  const [viewMode, setViewMode] = useState('live');
  const [delTarget, setDelTarget] = useState(null);
  // iter303ak-fix79 — "needs city clarification" filter + AI cleanup queue.
  const [needsCityFilter, setNeedsCityFilter] = useState(false);
  const [needsCityCount, setNeedsCityCount] = useState(null);
  const [showCityCleanup, setShowCityCleanup] = useState(false);

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown size={12} className="text-slate-300" />;
    return sortDir === 'asc' ? <ArrowUp size={12} className="text-[#D35400]" /> : <ArrowDown size={12} className="text-[#D35400]" />;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const flag = viewMode === 'all' ? '&include_deleted=true' : viewMode === 'deleted' ? '&only_deleted=true' : '';
      const cityFlag = needsCityFilter ? '&needs_city_review=true' : '';
      const { data } = await api.get(`/clients?page=${page}&limit=${limit}&search=${search}&sort_by=${sortBy}&sort_dir=${sortDir}${flag}${cityFlag}`);
      setClients(data.items);
      setTotal(data.total);
      setPages(data.pages);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, page, limit, search, sortBy, sortDir, viewMode, needsCityFilter]);

  useEffect(() => { load(); }, [load]);

  // Fetch the "needs city review" queue count (for chip badge) whenever
  // the filter is toggled or after a cleanup batch is applied.
  const refreshNeedsCityCount = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients?needs_city_review=true&limit=1`);
      setNeedsCityCount(data.total);
    } catch (e) { /* silent */ }
  }, [api]);

  useEffect(() => { refreshNeedsCityCount(); }, [refreshNeedsCityCount]);

  const restoreClient = async (c) => {
    try {
      await api.post(`/clients/${c._id}/restore`);
      toast.success('Client restored');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    }
  };

  // Load address dropdowns — refetch whenever the New/Edit Client modal opens
  // so the suburb/city pickers are always populated.
  // Iter197 — dropdowns now fetched inside ClientFormModal itself.

  const openEdit = (client) => {
    setEditForm(client);
    setShowForm(true);
  };

  // Auto-open Add form if ?add=1 in the URL (used by Workflow Runner)
  useEffect(() => {
    if (searchParams.get('add') === '1') {
      setEditForm({});
      setShowForm(true);
      searchParams.delete('add');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <div data-testid="clients-page" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Clients</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium"><span className="font-mono-num font-bold text-slate-700">{total.toLocaleString()}</span> {viewMode === 'deleted' ? 'deleted ' : viewMode === 'all' ? 'total (incl deleted) ' : 'total '}clients</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Iter178 — view-mode picker. */}
          <div data-testid="clients-view-mode" className="flex items-center gap-1 border border-[#E2E2DF] rounded-md p-0.5 bg-slate-50/50 text-xs">
            {[
              { k: 'live',    label: 'Live',          icon: <Eye size={11} /> },
              { k: 'all',     label: 'Incl. deleted', icon: <EyeOff size={11} /> },
              { k: 'deleted', label: 'Deleted',       icon: <Trash2 size={11} /> },
            ].map(opt => (
              <button
                key={opt.k}
                data-testid={`clients-view-${opt.k}`}
                onClick={() => { setViewMode(opt.k); setPage(1); }}
                className={`flex items-center gap-1 px-2.5 py-1 rounded font-semibold transition ${viewMode === opt.k ? 'bg-white text-[#D35400] shadow-sm border border-[#D35400]/30' : 'text-slate-500 hover:text-slate-800'}`}
              >
                {opt.icon} {opt.label}
              </button>
            ))}
          </div>
          <button data-testid="add-client-button" onClick={() => { setEditForm({}); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold transition-all duration-200 shadow-sm hover:shadow-md hover:shadow-orange-500/20">
            <Plus size={16} strokeWidth={2.5} /> Add Client
          </button>
        </div>
      </div>
      {/* iter303ak-fix79 — needs-city cleanup queue + AI lookup */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          data-testid="needs-city-filter"
          onClick={() => { setNeedsCityFilter(v => !v); setPage(1); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
            needsCityFilter
              ? 'bg-amber-100 border-amber-400 text-amber-900'
              : 'bg-white border-[#E2E2DF] text-slate-600 hover:border-amber-300 hover:text-amber-800'
          }`}
        >
          <MapPinOff size={12} /> Needs city clarification
          {needsCityCount != null && (
            <span
              data-testid="needs-city-count"
              className={`font-mono-num font-bold px-1.5 py-0.5 rounded-full text-[10px] ${
                needsCityFilter ? 'bg-amber-200 text-amber-900' : 'bg-slate-100 text-slate-700'
              }`}
            >
              {needsCityCount}
            </span>
          )}
        </button>
        {needsCityFilter && (
          <button
            data-testid="ai-city-cleanup-button"
            onClick={() => setShowCityCleanup(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border border-[#D35400] bg-[#D35400] text-white hover:bg-[#A04000] transition-colors shadow-sm"
          >
            <Sparkles size={12} /> AI-lookup cities
          </button>
        )}
        {needsCityFilter && (
          <span className="text-[11px] text-slate-500 italic ml-1">
            Rows where city is blank or a generic region label. Sort by suburb to work through geographically.
          </span>
        )}
      </div>
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="client-search-input" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search by C# (e.g. C1234), name, email, phone, suburb…" className="w-full pl-10 pr-4 py-2.5 border border-[#E2E2DF] rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]/30 focus:border-[#D35400] transition-all" />
      </div>
      {loading ? <div className="text-center py-12 text-slate-400">Loading...</div> : (
        <div className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50/80">
                <th className="text-left px-3 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 cursor-pointer select-none hover:text-[#D35400] transition-colors w-[80px]" onClick={() => toggleSort('client_no')}><span className="flex items-center gap-1.5">Ref <SortIcon field="client_no" /></span></th>
                <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 cursor-pointer select-none hover:text-[#D35400] transition-colors" onClick={() => toggleSort('name_first')}><span className="flex items-center gap-1.5">Name <SortIcon field="name_first" /></span></th>
                <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 hidden md:table-cell cursor-pointer select-none hover:text-[#D35400] transition-colors" onClick={() => toggleSort('email')}><span className="flex items-center gap-1.5">Contact <SortIcon field="email" /></span></th>
                <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 hidden lg:table-cell cursor-pointer select-none hover:text-[#D35400] transition-colors" onClick={() => toggleSort('address_suburb')}><span className="flex items-center gap-1.5">Location <SortIcon field="address_suburb" /></span></th>
                <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 hidden lg:table-cell">Company</th>
                <th className="text-center px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 hidden lg:table-cell">Jobs</th>
                <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 hidden xl:table-cell">Last Activity</th>
                <th className="text-right px-3 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 w-[80px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map(c => {
                const initials = getInitials(c.name_first, c.name_last) || (c.company_name?.[0] || '?').toUpperCase();
                const fullName = (c.name_first || c.name_last) ? `${c.name_first || ''} ${c.name_last || ''}`.trim() : (c.company_name || 'Unnamed');
                return (
                <tr key={c._id} data-testid={`client-row-${c._id}`} className={`border-t border-[#E2E2DF] cursor-pointer transition-colors group ${c.is_deleted ? 'bg-rose-50/40 hover:bg-rose-50/70' : 'hover:bg-orange-50/30'}`} onClick={() => navigate(`/clients/${c._id}`)}>
                  <td className="px-3 py-3 text-sm tabular-nums">
                    <div className="flex items-center gap-1.5">
                      {c.client_no ? <IdChip type="client" value={c.client_no} size="md" testId={`client-list-chip-${c._id}`} /> : <span className="text-slate-300">—</span>}
                      {c.is_deleted && <span data-testid={`client-deleted-badge-${c._id}`} className="text-[8px] font-bold uppercase tracking-wider text-rose-700 bg-rose-100 border border-rose-300 rounded-full px-1.5 py-0">Del</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 relative">
                    <div className="absolute inset-y-0 left-0 w-[2px] bg-[#D35400] opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#D35400]/15 to-[#D35400]/5 flex items-center justify-center flex-shrink-0 ring-1 ring-[#D35400]/10">
                        <span className="text-xs font-bold text-[#D35400] font-[Manrope]">{initials}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{fullName}</p>
                        <p className="text-xs text-slate-500 md:hidden truncate">{c.phone_mobile || c.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="space-y-1">
                      {c.email && <p className="text-xs text-slate-600 flex items-center gap-1.5"><Mail size={12} className="text-slate-400 flex-shrink-0" />{c.email}</p>}
                      {c.phone_mobile && <p className="text-xs text-slate-600 flex items-center gap-1.5 font-mono-num"><Phone size={12} className="text-slate-400 flex-shrink-0" />{c.phone_mobile}</p>}
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-sm text-slate-600">{c.address_suburb}{c.address_city ? `, ${c.address_city}` : ''}</td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {c.is_company && c.company_name ? <span className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 ring-1 ring-blue-100 flex items-center gap-1 w-fit max-w-[180px] truncate"><Building2 size={12} />{c.company_name}</span> : <span className="text-xs text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-center">
                    {c.job_count > 0 ? <span className="text-xs font-bold text-[#D35400] bg-orange-50 ring-1 ring-orange-100 px-2 py-0.5 rounded-full font-mono-num">{c.job_count}</span> : <span className="text-xs text-slate-300 font-mono-num">0</span>}
                  </td>
                  <td className="px-4 py-3 hidden xl:table-cell text-xs text-slate-500">
                    {c.last_activity || <span className="text-slate-300">—</span>}
                    {c.last_status && <span className="ml-1 text-[10px] text-slate-400">({c.last_status})</span>}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {c.is_deleted ? (
                      <button
                        data-testid={`restore-client-${c._id}`}
                        onClick={(e) => { e.stopPropagation(); restoreClient(c); }}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 border border-emerald-300 px-2 py-1 rounded hover:bg-emerald-50"
                      >
                        <RotateCcw size={11} /> Restore
                      </button>
                    ) : (
                      <button
                        data-testid={`delete-client-row-${c._id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDelTarget({
                            kind: 'client',
                            id: c._id,
                            label: c.is_company && c.company_name
                              ? c.company_name
                              : `${c.name_first || ''} ${c.name_last || ''}`.trim() || `Client ${c.client_no || ''}`,
                          });
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 text-[11px] font-semibold text-rose-600 border border-rose-200 px-2 py-1 rounded hover:bg-rose-50"
                        title="Delete client (soft or hard)"
                      >
                        <Trash2 size={11} /> Delete
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
              {clients.length === 0 && <tr><td colSpan={8} className="px-4 py-16 text-center"><p className="text-sm text-slate-400 mb-1">No clients found</p><p className="text-xs text-slate-300">Try adjusting your search</p></td></tr>}
            </tbody>
          </table>
          {pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E2DF] bg-slate-50/50">
              <span className="text-xs text-slate-500">Page <span className="font-mono-num font-semibold text-slate-700">{page}</span> of <span className="font-mono-num font-semibold text-slate-700">{pages}</span></span>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-1.5 rounded border border-[#E2E2DF] hover:bg-white hover:border-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><ChevronLeft size={16} /></button>
                <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="p-1.5 rounded border border-[#E2E2DF] hover:bg-white hover:border-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><ChevronRight size={16} /></button>
              </div>
            </div>
          )}
        </div>
      )}
      {showForm && (
        <ClientFormModal
          open={showForm}
          initial={editForm}
          onClose={() => { setShowForm(false); setEditForm({}); }}
          onSaved={(saved) => {
            const wasCreate = !editForm._id;
            setShowForm(false);
            setEditForm({});
            // Iter202 — after a CREATE, jump straight to the detail
            // page so the operator can see the auto-created property
            // (from the address in the form) and verify it before
            // adding more. This prevents the "Properties list stale"
            // → "Add Property again" → duplicate-property bug.
            if (wasCreate && (saved?._id || saved?.id)) {
              navigate(`/clients/${saved._id || saved.id}`);
            } else {
              load();
            }
          }}
        />
      )}
      {delTarget && (
        <DeleteConfirmDialog
          kind={delTarget.kind}
          entityId={delTarget.id}
          label={delTarget.label}
          api={api}
          onClose={() => setDelTarget(null)}
          onDeleted={() => { setDelTarget(null); load(); }}
        />
      )}
      <CityCleanupModal
        open={showCityCleanup}
        onClose={() => setShowCityCleanup(false)}
        onApplied={() => { load(); refreshNeedsCityCount(); }}
      />
    </div>
  );
}
